import React from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { ChevronRight, Menu, Search, UserRound, X } from 'lucide-react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';
import { MedcallClientListPage } from './MedcallClientListPage';
import { MedcallParticipantDetailPage } from './MedcallParticipantDetailPage';
import { MedcallSessionTimeout } from './MedcallSessionTimeout';
import {
  MEDCALL_ACCESS_POLICIES,
  MEDCALL_HEADER_LOGOUT_URL,
  MEDCALL_SESSION_LOGOUT_URL,
  getMedcallUserTools,
  isMedcallOnline,
  shouldRedirectFromMedcall2,
} from './medcallShellContract';
import './medcallLegacy.css';

const MEDCALL_BOOTSTRAP = gql`
  query MedcallBootstrap {
    medcallBootstrap {
      active
      canViewMonitoringReports
      defaultLandingPath
      diversionUserId
      drugDataProvider
      email
      fullName
      hasFacilityManagerAccess
      hasMedcallPowerAccess
      hasMedcallRouteAccess
      hasProviderEmailManagerAccess
      hasStandardAccess
      hasSuperAccess
      isAdminUser
      medcallUserId
      role
      routeBase
      username
    }
  }
`;

const MEDCALL_SHELL_SEARCH = gql`
  query MedcallShellSearch($searchText: String!) {
    medcallSearchParticipants(searchText: $searchText) {
      active
      aliases {
        firstname
        lastname
      }
      dob
      firstname
      id
      lastname
    }
  }
`;

type MedcallBootstrap = {
  active: boolean;
  canViewMonitoringReports: boolean;
  defaultLandingPath: string;
  diversionUserId: number;
  drugDataProvider: string;
  email: string;
  fullName: string;
  hasFacilityManagerAccess: boolean;
  hasMedcallPowerAccess: boolean;
  hasMedcallRouteAccess: boolean;
  hasProviderEmailManagerAccess: boolean;
  hasStandardAccess: boolean;
  hasSuperAccess: boolean;
  isAdminUser: boolean;
  medcallUserId: number;
  role: string;
  routeBase: string;
  username: string;
};

type MedcallBootstrapData = {
  medcallBootstrap: MedcallBootstrap;
};

type MedcallSearchParticipant = {
  active?: boolean | null;
  aliases?: Array<{
    firstname?: string | null;
    lastname?: string | null;
  }> | null;
  dob?: string | null;
  firstname?: string | null;
  id: number;
  lastname?: string | null;
};

type MedcallSearchData = {
  medcallSearchParticipants: MedcallSearchParticipant[];
};

type MedcallView = 'landing' | 'my-clients' | 'all-clients' | 'participant';
type AccessStateTone = 'warning' | 'error';
const MEDCALL_DEFAULT_ROUTE_BASE = '/tools/medcall';

// The legacy header uses Ant's filled-path UserOutlined, not a stroked person icon.
const LegacyUserIcon = () => (
  <svg aria-hidden="true" fill="currentColor" focusable="false" viewBox="64 64 896 896">
    <path d="M858.5 763.6a374 374 0 00-80.6-119.5 375.63 375.63 0 00-119.5-80.6c-.4-.2-.8-.3-1.2-.5C719.5 518 760 444.7 760 362c0-137-111-248-248-248S264 225 264 362c0 82.7 40.5 156 102.8 201.1-.4.2-.8.3-1.2.5-44.8 18.9-85 46-119.5 80.6a375.63 375.63 0 00-80.6 119.5A371.7 371.7 0 00136 901.8a8 8 0 008 8.2h60c4.4 0 7.9-3.5 8-7.8 2-77.2 33-149.5 87.9-204.3 56.7-56.7 132-87.9 212.2-87.9s155.5 31.2 212.2 87.9C779 752.7 810 825 812 902.2c.1 4.4 3.6 7.8 8 7.8h60a8 8 0 008-8.2c-1-47.8-10.9-94.3-29.5-138.2zM512 534c-45.9 0-89.1-17.9-121.6-50.4S340 407.9 340 362c0-45.9 17.9-89.1 50.4-121.6S466.1 190 512 190s89.1 17.9 121.6 50.4S684 316.1 684 362c0 45.9-17.9 89.1-50.4 121.6S557.9 534 512 534z" />
  </svg>
);

const normalizeValue = (value: unknown) => `${value || ''}`.trim().toLowerCase();

export const mapMedcallRoute = (routeBase: string, path: string | null | undefined) => {
  const normalizedPath = `${path || ''}`.trim();
  if (normalizedPath === routeBase || normalizedPath.startsWith(`${routeBase}/`)) {
    return normalizedPath;
  }

  if (
    normalizedPath === MEDCALL_DEFAULT_ROUTE_BASE
    || normalizedPath.startsWith(`${MEDCALL_DEFAULT_ROUTE_BASE}/`)
  ) {
    return `${routeBase}${normalizedPath.slice(MEDCALL_DEFAULT_ROUTE_BASE.length)}`;
  }

  return normalizedPath || `${routeBase}/all-clients`;
};

const formatSearchDate = (value: string | null | undefined) => {
  const match = `${value || ''}`.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return '';
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
};

const formatSearchAliases = (participant: MedcallSearchParticipant) => {
  if (!Array.isArray(participant.aliases)) {
    return '';
  }

  return participant.aliases
    .map((alias) => [alias.firstname, alias.lastname].filter(Boolean).join(' ').trim())
    .filter(Boolean)
    .join(', ');
};

const hasMedcallAccess = (user: any) => {
  return hasAnyPolicy(user, MEDCALL_ACCESS_POLICIES);
};

const getAccessToneStyle = (tone: AccessStateTone): React.CSSProperties => {
  if (tone === 'warning') {
    return {
      backgroundColor: 'var(--semantic-warning-soft)',
      borderColor: 'var(--semantic-warning-border-soft)',
    };
  }

  return {
    backgroundColor: 'var(--semantic-error-soft)',
    borderColor: 'var(--semantic-error-border-soft)',
  };
};

const AccessState = ({
  description,
  title,
  tone,
}: {
  description: string;
  title: string;
  tone: AccessStateTone;
}) => {
  return (
    <div className="p-4 md:p-6">
      <Card>
        <CardContent className="p-[var(--panel-padding)]">
          <div
            className="rounded-[var(--border-radius-medium)] border px-4 py-4"
            style={getAccessToneStyle(tone)}
          >
            <div className="space-y-1">
              <CardTitle className="text-lg">{title}</CardTitle>
              <p className="text-sm text-[var(--neutral-gray-100)]">{description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const MEDCALL2_BREADCRUMB_LABEL_KEY = 'medcall2BreadcrumbLabel';
const MEDCALL2_BREADCRUMB_PATH_KEY = 'medcall2BreadcrumbPath';

const MedcallLegacyBreadcrumb = ({ routeBase }: { routeBase: string }) => {
  const location = useLocation();
  const isParticipant = location.pathname.includes('/participants/');
  const isMyClients = location.pathname.includes('/my-clients');
  const listLabel = isMyClients ? 'My Clients' : 'All Clients';
  const listPath = isMyClients ? `${routeBase}/my-clients` : `${routeBase}/all-clients`;
  const locationState = location.state as { from?: string } | null;
  const stateOrigin = `${locationState?.from || ''}`;
  const savedOriginPath = window.localStorage.getItem(MEDCALL2_BREADCRUMB_PATH_KEY) || '';
  const participantOriginPath = stateOrigin.startsWith(`${routeBase}/`)
    ? stateOrigin
    : savedOriginPath.startsWith(`${routeBase}/`)
      ? savedOriginPath
      : `${routeBase}/all-clients`;
  const savedOriginLabel = window.localStorage.getItem(MEDCALL2_BREADCRUMB_LABEL_KEY);
  const participantOriginLabel = participantOriginPath.endsWith('/my-clients')
    ? 'My Clients'
    : savedOriginLabel === 'My Clients' && !stateOrigin
      ? 'My Clients'
      : 'All Clients';

  React.useEffect(() => {
    if (isParticipant) {
      return;
    }

    window.localStorage.setItem(MEDCALL2_BREADCRUMB_LABEL_KEY, listLabel);
    window.localStorage.setItem(MEDCALL2_BREADCRUMB_PATH_KEY, listPath);
  }, [isParticipant, listLabel, listPath]);

  return (
    <nav aria-label="breadcrumb" className="medcall-app__breadcrumb-nav">
      <ol className="medcall-app__breadcrumb">
        <li className="medcall-app__breadcrumb-item">
          <a href="/menu.php">
            <svg aria-hidden="true" viewBox="0 0 576 512">
              <path d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c.2 35.5-28.5 64.3-64 64.3H128.1c-35.3 0-64-28.7-64-64V287.7h-32c-18 0-32-14-32-32.1 0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L416 100.7V64c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32v121l52.8 46.4c8 7 12 15 11 24.1zM248 192c-13.3 0-24 10.7-24 24v80c0 13.3 10.7 24 24 24h80c13.3 0 24-10.7 24-24v-80c0-13.3-10.7-24-24-24h-80z" />
            </svg>
            Home
          </a>
        </li>
        {isParticipant ? (
          <>
            <li className="medcall-app__breadcrumb-item">
              <Link to={participantOriginPath}>{participantOriginLabel}</Link>
            </li>
            <li aria-current="page" className="medcall-app__breadcrumb-item medcall-app__breadcrumb-item--active">
              View
            </li>
          </>
        ) : (
          <li aria-current="page" className="medcall-app__breadcrumb-item medcall-app__breadcrumb-item--active">
            {listLabel}
          </li>
        )}
      </ol>
    </nav>
  );
};

const MedcallShell = ({
  bootstrap,
  children,
  routeBase,
}: {
  bootstrap: MedcallBootstrap;
  children: React.ReactNode;
  routeBase: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useUser();
  const [searchText, setSearchText] = React.useState('');
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobileViewport, setIsMobileViewport] = React.useState(() => window.innerWidth <= 767);
  const [searchParticipants, { data: searchData, loading: searchLoading }] = useLazyQuery<MedcallSearchData>(
    MEDCALL_SHELL_SEARCH,
    { fetchPolicy: 'network-only' }
  );
  const normalizedSearchText = searchText.trim().replace(/\s+/g, ' ');
  const searchResults = searchData?.medcallSearchParticipants || [];
  const myClientsSelected = location.pathname.includes('/my-clients');
  const allClientsSelected = location.pathname.includes('/all-clients');
  const online = isMedcallOnline(window.location.hostname);
  const managerAccess = {
    hasFacilityManagerAccess: bootstrap.hasFacilityManagerAccess,
    hasProviderEmailManagerAccess: bootstrap.hasProviderEmailManagerAccess,
  };
  const desktopUserTools = getMedcallUserTools(managerAccess, false);
  const mobileUserTools = getMedcallUserTools(managerAccess, true);

  React.useEffect(() => {
    if (routeBase !== '/tools/medcall2' || !userMenuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !userMenuRef.current?.contains(event.target)) setUserMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [routeBase, userMenuOpen]);

  React.useEffect(() => {
    document.body.classList.add('medcall-legacy-active');
    if (routeBase === '/tools/medcall2') document.body.classList.add('medcall2-active');
    // Native date inputs open from their entire surface in the legacy workflow.
    const openDatePicker = (event: MouseEvent) => {
      if (routeBase !== '/tools/medcall2') return;
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!['date', 'datetime-local'].includes(input.type) || input.disabled || input.readOnly) return;
      if (typeof input.showPicker !== 'function' || !event.isTrusted) return;
      input.showPicker();
    };
    document.addEventListener('click', openDatePicker);
    return () => {
      document.body.classList.remove('medcall-legacy-active');
      document.body.classList.remove('medcall2-active');
      document.removeEventListener('click', openDatePicker);
    };
  }, [routeBase]);

  React.useEffect(() => {
    const updateViewport = () => setIsMobileViewport(window.innerWidth <= 767);
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  React.useEffect(() => {
    if (normalizedSearchText.length < 3) {
      return;
    }

    const timer = window.setTimeout(() => {
      void searchParticipants({ variables: { searchText: normalizedSearchText } });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [normalizedSearchText, searchParticipants]);

  const handleSessionLogout = () => {
    logout();
    window.location.assign(MEDCALL_SESSION_LOGOUT_URL);
  };

  const handleSearchSelection = (participant: MedcallSearchParticipant) => {
    setSearchText(`${participant.firstname || ''} ${participant.lastname || ''}`.trim());
    navigate(`${routeBase}/participants/${participant.id}`);
  };

  return (
    <div className={`medcall-app${routeBase === MEDCALL_DEFAULT_ROUTE_BASE && (allClientsSelected || location.pathname.includes('/participants/')) ? ' medcall-app--wide' : ''}`} data-testid="medcall-legacy-shell">
      <header className="medcall-app__header">
        <Link aria-label="MedCall home" className="medcall-app__brand" to={routeBase}>
          <img alt="Los Angeles County seal" src={`${process.env.PUBLIC_URL}/lacountyseal.png`} />
          <h1>MedCall</h1>
        </Link>

        <div className="medcall-app__desktop-user-tools">
          <div className="medcall-app__user-menu-wrap" ref={userMenuRef}>
            <button
              aria-expanded={userMenuOpen}
              aria-label="Open user tools"
              className="medcall-app__avatar-button"
              onClick={() => setUserMenuOpen((open) => !open)}
              type="button"
            >
              {routeBase === '/tools/medcall2' ? <LegacyUserIcon /> : <UserRound aria-hidden="true" />}
            </button>
            {userMenuOpen ? (
              <div className="medcall-app__user-popover" role="menu">
                <strong>
                  {bootstrap.fullName || bootstrap.username}
                  {!online ? <span className="medcall-app__offline-badge">OFFLINE</span> : null}
                </strong>
                {routeBase === '/tools/medcall2' ? (
                  <ul className="medcall2-user-menu-list">
                    {desktopUserTools.map((tool) => (
                      <li key={tool.href}>
                        <a href={tool.href} onClick={() => setUserMenuOpen(false)} role="menuitem">{tool.label}</a>
                      </li>
                    ))}
                  </ul>
                ) : desktopUserTools.map((tool) => (
                  <a href={tool.href} key={tool.href} onClick={() => setUserMenuOpen(false)} role="menuitem">
                    {tool.label}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
          <a className="medcall-app__logout-button" href={MEDCALL_HEADER_LOGOUT_URL} onClick={logout}>
            Log Out
          </a>
        </div>

        <button
          aria-expanded={mobileMenuOpen}
          aria-label="Open user tools"
          className="medcall-app__mobile-menu-button"
          onClick={() => setMobileMenuOpen(true)}
          type="button"
        >
          <Menu aria-hidden="true" />
        </button>
      </header>

      {mobileMenuOpen ? (
        <div className="medcall-app__mobile-drawer-backdrop" onClick={() => setMobileMenuOpen(false)} role="presentation">
          <aside
            aria-label="User tools"
            className="medcall-app__mobile-drawer"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="medcall-app__mobile-drawer-heading">
              <span className="medcall-app__avatar-button" aria-hidden="true">
                {routeBase === '/tools/medcall2' ? <LegacyUserIcon /> : <UserRound />}
              </span>
              <button aria-label="Close user tools" onClick={() => setMobileMenuOpen(false)} type="button"><X /></button>
            </div>
            <div className="medcall-app__mobile-drawer-menu">
              {mobileUserTools.map((tool) => (
                <a href={tool.href} key={tool.href} onClick={() => setMobileMenuOpen(false)}>
                  <span>{tool.label}</span>
                  {tool.showChevron ? <ChevronRight aria-hidden="true" /> : null}
                </a>
              ))}
            </div>
            <a className="medcall-app__logout-button" href={MEDCALL_HEADER_LOGOUT_URL} onClick={logout}>Log Out</a>
          </aside>
        </div>
      ) : null}

      {routeBase === '/tools/medcall2' ? <MedcallLegacyBreadcrumb routeBase={routeBase} /> : null}

      <div className="medcall-app__nav-search">
        <nav aria-label="MedCall clients" className="medcall-app__tabs">
          <Link aria-current={myClientsSelected ? 'page' : undefined} to={`${routeBase}/my-clients`}>My Clients</Link>
          <Link aria-current={allClientsSelected ? 'page' : undefined} to={`${routeBase}/all-clients`}>All Clients</Link>
        </nav>

        <div className="medcall-app__search-wrap">
          <Search aria-hidden="true" className="medcall-app__search-icon" />
          <input
            aria-label="Search MedCall clients"
            autoComplete="off"
            onChange={(event) => setSearchText(event.target.value)}
            placeholder={routeBase === '/tools/medcall2' && isMobileViewport
              ? 'Search MedCall clients (3 characters)'
              : 'Search MedCall clients (3 or more characters)'}
            value={searchText}
          />
          {normalizedSearchText.length > 0 ? (
            <button aria-label="Clear client search" className="medcall-app__search-clear" onClick={() => setSearchText('')} type="button">
              <X aria-hidden="true" />
            </button>
          ) : null}
          {normalizedSearchText.length > 0 ? (
            <div className="medcall-app__search-results" role="listbox">
              {normalizedSearchText.length < 3 ? <div className="medcall-app__search-message">Please enter 3 or more characters</div> : null}
              {normalizedSearchText.length >= 3 && searchLoading ? <div className="medcall-app__search-message">Searching…</div> : null}
              {normalizedSearchText.length >= 3 && !searchLoading && searchResults.length < 1 ? (
                <div className="medcall-app__search-message">No Client was found</div>
              ) : null}
              {normalizedSearchText.length >= 3 && !searchLoading ? searchResults.map((participant) => {
                const aliases = formatSearchAliases(participant);
                return (
                  <button
                    aria-selected="false"
                    key={participant.id}
                    onClick={() => handleSearchSelection(participant)}
                    role="option"
                    type="button"
                  >
                    <span>{participant.firstname} {participant.lastname}</span>
                    <em>({formatSearchDate(participant.dob)})</em>
                    {!participant.active ? <strong> - Deactivated</strong> : null}
                    {aliases ? <small title={aliases}> - {aliases}</small> : null}
                  </button>
                );
              }) : null}
            </div>
          ) : null}
        </div>
      </div>

      <main className="medcall-app__content">
        {children}
      </main>
      <MedcallSessionTimeout onLogout={handleSessionLogout} />
    </div>
  );
};

const MedcallLegacyAccessRedirect = () => {
  React.useEffect(() => {
    window.location.assign('/menu.php');
  }, []);

  return <OdrLoadingSpinner />;
};

const MedcallRoutePage = ({
  routeBase = MEDCALL_DEFAULT_ROUTE_BASE,
  view,
}: {
  routeBase?: string;
  view: MedcallView;
}) => {
  const { user, isUserLoading } = useUser();
  const canAccess = hasMedcallAccess(user);

  const { data, error, loading } = useQuery<MedcallBootstrapData>(MEDCALL_BOOTSTRAP, {
    fetchPolicy: 'cache-and-network',
    skip: !canAccess,
  });

  if (isUserLoading || (canAccess && loading)) {
    return <OdrLoadingSpinner />;
  }

  if (!canAccess) {
    return (
      <AccessState
        tone="warning"
        title="Access Denied"
        description="MedCall requires MedCall access or admin access."
      />
    );
  }

  if (error) {
    const normalizedMessage = normalizeValue(error.message);
    if (normalizedMessage.includes('unauthorized')) {
      return (
        <AccessState
          tone="warning"
          title="Access Denied"
          description="MedCall requires MedCall access or admin access."
        />
      );
    }

    return (
      <AccessState
        tone="error"
        title="Unable to Load MedCall"
        description={error.message}
      />
    );
  }

  const bootstrap = data?.medcallBootstrap;
  if (!bootstrap) {
    return (
      <AccessState
        tone="error"
        title="Unable to Load MedCall"
        description="MedCall is unavailable right now."
      />
    );
  }

  if (shouldRedirectFromMedcall2(routeBase, bootstrap.hasMedcallRouteAccess)) {
    return <MedcallLegacyAccessRedirect />;
  }

  if (view === 'landing') {
    return <Navigate to={mapMedcallRoute(routeBase, bootstrap.defaultLandingPath)} replace />;
  }

  if (view === 'my-clients') {
    return (
      <MedcallShell bootstrap={bootstrap} routeBase={routeBase}>
        <MedcallClientListPage
          bootstrap={bootstrap}
          routeBase={routeBase}
          scope="my"
        />
      </MedcallShell>
    );
  }

  if (view === 'all-clients') {
    return (
      <MedcallShell bootstrap={bootstrap} routeBase={routeBase}>
        <MedcallClientListPage
          bootstrap={bootstrap}
          routeBase={routeBase}
          scope="all"
        />
      </MedcallShell>
    );
  }

  return (
    <MedcallShell bootstrap={bootstrap} routeBase={routeBase}>
      <MedcallParticipantDetailPage bootstrap={bootstrap} routeBase={routeBase} />
    </MedcallShell>
  );
};

export const MedcallLandingRedirect = () => {
  return <MedcallRoutePage view="landing" />;
};

export const MedcallMyClientsPage = () => {
  return <MedcallRoutePage view="my-clients" />;
};

export const MedcallAllClientsPage = () => {
  return <MedcallRoutePage view="all-clients" />;
};

export const MedcallParticipantPage = () => {
  return <MedcallRoutePage view="participant" />;
};

export const Medcall2LandingRedirect = () => {
  return <MedcallRoutePage routeBase="/tools/medcall2" view="landing" />;
};

export const Medcall2MyClientsPage = () => {
  return <MedcallRoutePage routeBase="/tools/medcall2" view="my-clients" />;
};

export const Medcall2AllClientsPage = () => {
  return <MedcallRoutePage routeBase="/tools/medcall2" view="all-clients" />;
};

export const Medcall2ParticipantPage = () => {
  return <MedcallRoutePage routeBase="/tools/medcall2" view="participant" />;
};
